import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  useImportBiometrics,
  useInspectBiometrics,
  useSaveBiometricLayout,
} from "../../../../api/queries/attendanceQueries";
import { getApiErrorMessage } from "../../../../utils/apiError";
import {
  DATE_ORDERS,
  DEFAULT_EMPLOYEE_PATTERN,
  EMPLOYEE_MATCH,
  LAYOUTS,
  ROLES,
  ROLE_COLORS,
  assignColumn,
  cleanConfig,
  columnLetter,
  configProblems,
  punchColumnsOf,
  rolesOfColumn,
  splitValues,
  toEditable,
} from "./bioConfig";
import DryRunResult from "./DryRunResult";

const EMPTY = [];
const messagesOf = (err, fallback) => {
  const m = err?.response?.data?.message;
  return Array.isArray(m) ? m : [getApiErrorMessage(err, fallback)];
};
const roleLabel = (layout, tag) => {
  const [key, i] = tag.split(":");
  const def = (ROLES[layout] || []).find((r) => r.key === key);
  return def
    ? `${def.label}${i !== undefined ? ` ${Number(i) + 1}` : ""}`
    : key;
};

/**
 * Create / edit a biometric layout: upload a sample, click columns to say
 * what they hold, pick the layout options, save, then test it on the sample.
 */
const LayoutBuilder = ({
  layout,
  presets = EMPTY,
  systems = EMPTY,
  onDone,
}) => {
  const [name, setName] = useState(layout?.name || "");
  const [system, setSystem] = useState(layout?.system || "");
  const [isDefault, setIsDefault] = useState(!!layout?.isDefault);
  const [config, setConfig] = useState(() => toEditable(layout?.config));
  const [touched, setTouched] = useState(!!layout);
  const [sample, setSample] = useState(null);
  const [grid, setGrid] = useState(null);
  const [activeRole, setActiveRole] = useState(null);
  const [errors, setErrors] = useState([]);
  const [test, setTest] = useState(null);
  const fileRef = useRef(null);

  const inspect = useInspectBiometrics();
  const save = useSaveBiometricLayout();
  const tester = useImportBiometrics();

  const set = (changes) => {
    setTouched(true);
    setConfig((c) => ({ ...c, ...changes }));
  };
  const roles = ROLES[config.layout] || EMPTY;
  const problems = configProblems(config);
  const punchCols = punchColumnsOf(config);
  const rowLayout = config.layout !== "EMPLOYEE_BLOCKS";

  const runInspect = (file, sheet) =>
    inspect.mutate(
      { file, sheet },
      {
        onSuccess: (res) => {
          setGrid(res);
          if (res.sheet && res.sheet !== config.sheet)
            setConfig((c) => ({ ...c, sheet: res.sheet }));
          // A fresh layout follows what the server detected
          if (!touched && res.detectedLayout && LAYOUTS[res.detectedLayout])
            setConfig((c) => ({ ...c, layout: res.detectedLayout }));
          setActiveRole(
            (ROLES[res.detectedLayout || config.layout] || ROLES.PUNCH_ROWS)[0]
              .key,
          );
        },
        onError: (err) =>
          toast.error(messagesOf(err, "Could not read the sample.").join(" ")),
      },
    );

  const applyPreset = (key) => {
    const p = presets.find((x) => x.key === key);
    if (!p) return;
    setConfig((c) => ({ ...toEditable(p.config), sheet: c.sheet }));
    setTouched(true);
    if (!name.trim()) setName(p.name);
    setActiveRole((ROLES[p.config?.layout] || ROLES.PUNCH_ROWS)[0].key);
  };

  const columns = grid?.columns?.length
    ? grid.columns
    : Array.from(
        { length: Math.max(0, ...(grid?.rows || EMPTY).map((r) => r.length)) },
        (_, i) => columnLetter(i),
      );

  // Dry run with the layout as it is on screen (not saved)
  const testUnsaved = () => {
    setErrors([]);
    tester.mutate(
      {
        file: sample,
        dryRun: true,
        config: cleanConfig(config),
        system: system.trim() || undefined,
      },
      {
        onSuccess: setTest,
        onError: (err) =>
          setErrors(messagesOf(err, "Could not test the layout.")),
      },
    );
  };

  const doSave = () => {
    if (!name.trim()) return setErrors(["Give the layout a name."]);
    if (!system.trim())
      return setErrors(["Enter the device / software name (system)."]);
    setErrors([]);
    save.mutate(
      {
        id: layout?.id || undefined,
        name: name.trim(),
        system: system.trim(),
        isDefault,
        config: cleanConfig(config),
      },
      {
        onSuccess: () => {
          toast.success("Layout saved.");
          onDone();
        },
        onError: (err) =>
          setErrors(messagesOf(err, "Could not save the layout.")),
      },
    );
  };

  return (
    <section className="att-card">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h2 className="bio-subtitle m-0">
          {layout?.id ? `Edit layout: ${layout.name}` : "New layout"}
        </h2>
        <button
          type="button"
          className="btn btn-sm btn-light border"
          onClick={onDone}
        >
          <i className="bi bi-x-lg me-1"></i>Close
        </button>
      </div>

      {errors.length > 0 && (
        <div className="alert alert-danger fs-085 py-2">
          {errors.length === 1 ? (
            errors[0]
          ) : (
            <ul className="mb-0 ps-3">
              {errors.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <label htmlFor="bl-name" className="profile-label">
            Name <span className="text-danger">*</span>
          </label>
          <input
            id="bl-name"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="bl-system" className="profile-label">
            Device / software <span className="text-danger">*</span>
          </label>
          <input
            id="bl-system"
            className="form-control"
            value={system}
            onChange={(e) => setSystem(e.target.value)}
            list="bl-systems"
            placeholder="e.g. ZKTeco"
            maxLength={100}
          />
          <datalist id="bl-systems">
            {systems.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <div className="fs-070 text-slate-500 mt-1">
            Biometric IDs are linked per device system.
          </div>
        </div>
        <div className="col-md-4">
          <label htmlFor="bl-preset" className="profile-label">
            Start from preset
          </label>
          <select
            id="bl-preset"
            className="form-select"
            value=""
            onChange={(e) => applyPreset(e.target.value)}
          >
            <option value="">Choose a preset…</option>
            {presets.map((p) => (
              <option key={p.key} value={p.key}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12">
          <div className="form-check form-switch">
            <input
              id="bl-default"
              type="checkbox"
              className="form-check-input"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            <label htmlFor="bl-default" className="form-check-label fs-085">
              Default layout for uploads
            </label>
          </div>
        </div>
      </div>

      {/* Sample */}
      <h3 className="bio-subtitle">1. Sample file</h3>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <input
          ref={fileRef}
          type="file"
          className="form-control bio-file"
          accept=".csv,.txt,.xlsx,.xls"
          aria-label="Sample file"
          onChange={(e) => {
            const f = e.target.files?.[0] || null;
            setSample(f);
            setTest(null);
            if (f) runInspect(f, config.sheet || undefined);
          }}
        />
        {grid?.sheets?.length > 1 && (
          <select
            className="form-select w-auto"
            value={grid.sheet || ""}
            onChange={(e) => runInspect(sample, e.target.value)}
            aria-label="Sheet"
          >
            {grid.sheets.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        )}
        {inspect.isPending && (
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-label="Reading"
          ></span>
        )}
        {grid && (
          <span className="fs-075 text-slate-500">
            {(grid.totalRows ?? 0).toLocaleString()} rows; first{" "}
            {(grid.rows || EMPTY).length} shown.
          </span>
        )}
      </div>
      {grid?.suggestedPreset &&
        !touched &&
        presets.some((p) => p.key === grid.suggestedPreset) && (
          <div className="alert alert-info fs-080 py-2 d-flex flex-wrap align-items-center gap-2">
            <i className="bi bi-magic"></i> This looks like the{" "}
            <strong>
              {presets.find((p) => p.key === grid.suggestedPreset).name}
            </strong>{" "}
            layout.
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => applyPreset(grid.suggestedPreset)}
            >
              Use it
            </button>
          </div>
        )}

      {/* Options */}
      <h3 className="bio-subtitle mt-3">2. How the file is laid out</h3>
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <label htmlFor="bl-layout" className="profile-label">
            Layout
          </label>
          <select
            id="bl-layout"
            className="form-select"
            value={config.layout}
            onChange={(e) => {
              set({ layout: e.target.value });
              setActiveRole(ROLES[e.target.value][0].key);
            }}
          >
            {Object.entries(LAYOUTS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          <div className="fs-070 text-slate-500 mt-1">
            {LAYOUTS[config.layout]?.help}
          </div>
        </div>
        <div className="col-md-3">
          <label htmlFor="bl-match" className="profile-label">
            Match employees by
          </label>
          <select
            id="bl-match"
            className="form-select"
            value={config.employeeMatch}
            onChange={(e) => set({ employeeMatch: e.target.value })}
          >
            {Object.entries(EMPLOYEE_MATCH).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label htmlFor="bl-dates" className="profile-label">
            Date order
          </label>
          <select
            id="bl-dates"
            className="form-select"
            value={config.dateOrder}
            onChange={(e) => set({ dateOrder: e.target.value })}
          >
            {Object.entries(DATE_ORDERS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        {rowLayout && (
          <div className="col-md-1">
            <label htmlFor="bl-header" className="profile-label">
              Header row
            </label>
            <input
              id="bl-header"
              type="number"
              min="0"
              className="form-control"
              value={config.headerRow}
              title="0 = no header row"
              onChange={(e) => set({ headerRow: e.target.value })}
            />
          </div>
        )}
        <div className="col-md-2 d-flex align-items-end">
          <div className="form-check form-switch">
            <input
              id="bl-overnight"
              type="checkbox"
              className="form-check-input"
              checked={config.overnight !== false}
              onChange={(e) => set({ overnight: e.target.checked })}
            />
            <label htmlFor="bl-overnight" className="form-check-label fs-080">
              Overnight shifts
            </label>
          </div>
        </div>
        {config.layout === "EMPLOYEE_BLOCKS" && (
          <div className="col-md-8">
            <label htmlFor="bl-pattern" className="profile-label">
              Employee header pattern{" "}
              <span className="text-slate-400 fw-normal">
                (optional regex with groups id / name)
              </span>
            </label>
            <input
              id="bl-pattern"
              className="form-control font-monospace"
              value={config.employeePattern}
              onChange={(e) => set({ employeePattern: e.target.value })}
              placeholder={`${DEFAULT_EMPLOYEE_PATTERN}   (default: "NAME (ID)")`}
            />
          </div>
        )}
      </div>

      {/* Columns */}
      <h3 className="bio-subtitle">3. Click the columns</h3>
      <div
        className="d-flex flex-wrap gap-2 mb-2"
        role="radiogroup"
        aria-label="Column role to assign"
      >
        {roles.map((r) => {
          const assigned = r.top ? config[r.key] : config.columns?.[r.key];
          const shown = Array.isArray(assigned)
            ? assigned.join(", ")
            : assigned;
          return (
            <button
              key={r.key}
              type="button"
              role="radio"
              aria-checked={activeRole === r.key}
              className={`bio-role ${activeRole === r.key ? "active" : ""}`}
              style={{ "--role-color": ROLE_COLORS[r.key] }}
              onClick={() => setActiveRole(r.key)}
            >
              <span className="bio-role-dot" aria-hidden="true"></span>
              {r.label}
              {r.multi ? " (several)" : ""}: <strong>{shown || "—"}</strong>
            </button>
          );
        })}
      </div>
      <p className="fs-075 text-slate-500 mb-2">
        Pick a role above, then click a column letter. Click again to clear it.
        {rowLayout
          ? Number(config.headerRow) === 0
            ? " No header row."
            : ` Row ${config.headerRow} is the header row.`
          : ""}
        {!grid &&
          " Upload a sample to see the columns, or type nothing and pick a preset."}
      </p>

      {grid && (
        <div className="bio-grid-wrap">
          <table className="bio-grid">
            <thead>
              <tr>
                <th className="bio-grid-rownum">#</th>
                {columns.map((letter) => {
                  const tags = rolesOfColumn(config, letter);
                  return (
                    <th
                      key={letter}
                      style={
                        tags.length
                          ? {
                              "--role-color":
                                ROLE_COLORS[tags[0].split(":")[0]],
                            }
                          : undefined
                      }
                      className={tags.length ? "is-assigned" : ""}
                    >
                      <button
                        type="button"
                        className="bio-grid-col"
                        onClick={() => {
                          if (!activeRole) return;
                          setTouched(true);
                          setConfig((c) => assignColumn(c, activeRole, letter));
                        }}
                        aria-label={`Column ${letter}${tags.length ? `: ${tags.map((t) => roleLabel(config.layout, t)).join(", ")}` : ""}`}
                      >
                        {letter}
                      </button>
                      {tags.map((t) => (
                        <span key={t} className="bio-grid-tag">
                          {roleLabel(config.layout, t)}
                        </span>
                      ))}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {(grid.rows || EMPTY).map((row, ri) => (
                <tr
                  key={ri}
                  className={
                    rowLayout && ri + 1 === Number(config.headerRow)
                      ? "is-header"
                      : ""
                  }
                >
                  <td className="bio-grid-rownum">{ri + 1}</td>
                  {columns.map((letter, ci) => {
                    const tags = rolesOfColumn(config, letter);
                    return (
                      <td
                        key={letter}
                        className={tags.length ? "is-assigned" : ""}
                        style={
                          tags.length
                            ? {
                                "--role-color":
                                  ROLE_COLORS[tags[0].split(":")[0]],
                              }
                            : undefined
                        }
                      >
                        {row[ci] === null || row[ci] === undefined
                          ? ""
                          : String(row[ci])}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* IN / OUT */}
      {config.layout === "PUNCH_ROWS" && config.columns?.type && (
        <div className="row g-2 mt-3">
          <div className="col-md-6">
            <label htmlFor="bl-in" className="profile-label">
              Values that mean IN
            </label>
            <input
              id="bl-in"
              className="form-control"
              value={(config.typeValues?.in || EMPTY).join(", ")}
              onChange={(e) =>
                set({
                  typeValues: {
                    ...config.typeValues,
                    in: splitValues(e.target.value),
                  },
                })
              }
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="bl-out" className="profile-label">
              Values that mean OUT
            </label>
            <input
              id="bl-out"
              className="form-control"
              value={(config.typeValues?.out || EMPTY).join(", ")}
              onChange={(e) =>
                set({
                  typeValues: {
                    ...config.typeValues,
                    out: splitValues(e.target.value),
                  },
                })
              }
            />
          </div>
        </div>
      )}
      {punchCols.length > 0 && (
        <div className="mt-3">
          <div className="profile-label">Time columns are</div>
          <div className="d-flex flex-wrap gap-3 mb-2">
            {[
              ["ALTERNATE", "IN, OUT, IN, OUT…"],
              ["NONE", "Not marked (the server pairs them)"],
              ["PER_COLUMN", "Set per column"],
            ].map(([k, label]) => (
              <label key={k} className="form-check fs-080">
                <input
                  type="radio"
                  className="form-check-input"
                  name="bl-punch-types"
                  checked={
                    k === "PER_COLUMN"
                      ? Array.isArray(config.punchTypes)
                      : config.punchTypes === k
                  }
                  onChange={() =>
                    set({
                      punchTypes:
                        k === "PER_COLUMN"
                          ? punchCols.map((_, i) =>
                              i % 2 === 0 ? "IN" : "OUT",
                            )
                          : k,
                    })
                  }
                />
                <span className="form-check-label">{label}</span>
              </label>
            ))}
          </div>
          {Array.isArray(config.punchTypes) && (
            <div className="d-flex flex-wrap gap-2">
              {punchCols.map((letter, i) => (
                <label
                  key={letter}
                  className="d-flex align-items-center gap-1 fs-080"
                >
                  <strong>{letter}</strong>
                  <select
                    className="form-select form-select-sm w-auto"
                    value={config.punchTypes[i] || ""}
                    onChange={(e) =>
                      set({
                        punchTypes: config.punchTypes.map((t, j) =>
                          j === i ? e.target.value || null : t,
                        ),
                      })
                    }
                  >
                    <option value="IN">IN</option>
                    <option value="OUT">OUT</option>
                    <option value="">Either</option>
                  </select>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {problems.length > 0 && (
        <ul className="fs-080 text-warning-emphasis mt-3 mb-0 ps-3">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}

      <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
        <button
          type="button"
          className="btn btn-outline-secondary profile-btn"
          onClick={onDone}
          disabled={save.isPending}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-outline-primary profile-btn bg-white"
          onClick={testUnsaved}
          disabled={tester.isPending || !sample || problems.length > 0}
          title={!sample ? "Upload a sample first" : undefined}
        >
          {tester.isPending && (
            <span
              className="spinner-border spinner-border-sm"
              aria-hidden="true"
            ></span>
          )}
          <i className="bi bi-search"></i> Test on sample
        </button>
        <button
          type="button"
          className="btn btn-primary profile-btn"
          onClick={doSave}
          disabled={save.isPending || problems.length > 0}
        >
          {save.isPending && (
            <span
              className="spinner-border spinner-border-sm"
              aria-hidden="true"
            ></span>
          )}
          Save
        </button>
      </div>

      {test && (
        <div className="mt-3">
          <h3 className="bio-subtitle">Test result (nothing saved)</h3>
          <DryRunResult result={test} />
        </div>
      )}
    </section>
  );
};

export default LayoutBuilder;
